import {
  flow,
  Instance,
  types,
  detach,
  getSnapshot,
  isAlive,
} from 'mobx-state-tree';
import { withEnvironment } from '../extensions/with-environment';
import {
  ExploreElement,
  ExploreElementModel,
  ExploreElementIn,
  ExploreElementCreateIn,
} from '../ExploreElementModel/ExploreElementModel';
import { Area } from '../../services/Database/CloudStorageService';
import { logger } from '../../services/Logger';

export const ExploreElementStoreModel = types
  .model('ExploreElementStore', {
    elements: types.array(ExploreElementModel),
    isLoading: types.optional(types.boolean, false),
    lastUpdated: types.optional(types.Date, () => new Date()),
  })
  .extend(withEnvironment)
  .views((self) => ({
    get favorites() {
      return self.elements.filter((element) => element.favorite);
    },
    get byType() {
      return (type: string) =>
        self.elements.filter((element) => element.type === type);
    },
    get byUser() {
      return (uid: string) =>
        self.elements.filter((element) => {
          const elementData = element.toServer('');
          return elementData.uid === uid;
        });
    },
    get searchElements() {
      return (searchTerm: string) =>
        self.elements.filter((element) => {
          const term = searchTerm.toLowerCase();
          return (
            element.name.toLowerCase().includes(term) ||
            element.country.toLowerCase().includes(term) ||
            element.location.toLowerCase().includes(term) ||
            element.nationality.toLowerCase().includes(term)
          );
        });
    },
    getElementById(id: string) {
      return self.elements.find((element) => element.id === id);
    },
    get aliveElements() {
      return self.elements.filter((element) => isAlive(element));
    },
  }))
  .actions((self) => {
    const addElement = (element: ExploreElement) => {
      try {
        // Validiere Element ID
        if (!element.id || /^\d+$/.test(element.id)) {
          logger.error('🚨 Attempting to add element with invalid ID:', {
            id: element.id,
            name: element.name,
            idType: typeof element.id,
          });
          throw new Error(`Invalid element ID: ${element.id}`);
        }

        const existingIndex = self.elements.findIndex((e) => {
          try {
            return isAlive(e) && e.id === element.id;
          } catch {
            return false;
          }
        });

        if (existingIndex >= 0) {
          // ✅ Sicheres Update
          const existingElement = self.elements[existingIndex];
          if (isAlive(existingElement)) {
            detach(existingElement);
          }
          self.elements.splice(existingIndex, 1, element);
          logger.debug(`🔄 Updated element: ${element.name} (${element.id})`);
        } else {
          // Add new element
          self.elements.push(element);
          logger.debug(`➕ Added new element: ${element.name} (${element.id})`);
        }

        updateLastUpdated();
      } catch (error) {
        logger.error('❌ Failed to add element:', error);
        throw error;
      }
    };

    const removeElement = (id: string) => {
      try {
        const element = self.elements.find((el) => {
          try {
            return isAlive(el) && el.id === id;
          } catch {
            return false;
          }
        });

        if (element) {
          if (isAlive(element)) {
            detach(element);
          }
          self.elements.remove(element);
          logger.debug(`🗑️ Removed element: ${id}`);
        }
      } catch (error) {
        logger.error('❌ Failed to remove element:', error);
      }
    };

    const clearAll = () => {
      try {
        // Detach alle lebenden Elemente
        const elementsToRemove = [...self.elements];
        elementsToRemove.forEach((element) => {
          try {
            if (isAlive(element)) {
              detach(element);
            }
          } catch (error) {
            logger.warn('Warning during element detach:', error);
          }
        });

        self.elements.clear();
        logger.debug('🧹 Cleared all elements safely');
      } catch (error) {
        logger.error('❌ Failed to clear elements:', error);
      }
    };

    const setLoading = (loading: boolean) => {
      self.isLoading = loading;
    };

    const updateLastUpdated = () => {
      self.lastUpdated = new Date();
    };

    const loadElements = flow(function* (area?: Area) {
      try {
        setLoading(true);
        logger.debug('Loading ExploreElements from Firebase...');

        const cloudStorage = self.environment.cloudStorage;
        if (!cloudStorage) {
          throw new Error('CloudStorage not available');
        }

        const elementsData: any[] = yield cloudStorage.getExploreElements(area);

        // ✅ Debug: Zeige die IDs die von Firebase kommen
        logger.debug(
          '📊 Raw Firestore data:',
          elementsData.map((e) => ({
            id: e.id,
            name: e.name,
            idType: typeof e.id,
            isNumeric: /^\d+$/.test(e.id || ''),
          }))
        );

        // Clear existing elements safely
        const elementsToRemove = [...self.elements];
        elementsToRemove.forEach((element) => {
          if (isAlive(element)) {
            detach(element);
          }
        });
        clearAll();

        // Logging: Rohdaten aus Firestore
        logger.debug('Firestore raw elementData:', elementsData);
        // Convert data to ExploreElement instances and add them
        elementsData.forEach((elementData) => {
          const parsedElementData = deepParseDates(elementData);
          try {
            const element = ExploreElementModel.create(
              parsedElementData,
              self.environment
            );
            self.elements.push(element);
          } catch (err) {
            logger.error(
              '❌ Fehler beim Erstellen von ExploreElement:',
              err,
              parsedElementData
            );
          }
        });

        updateLastUpdated();
        logger.debug(
          `✅ Loaded ${self.elements.length} ExploreElements with valid IDs`
        );

        // ✅ Debug: Zeige finale IDs im Store
        logger.debug(
          '📦 Final store element IDs:',
          self.elements.map((e) => ({
            id: e.id,
            name: e.name,
            isAlive: isAlive(e),
          }))
        );
      } catch (error) {
        logger.error('❌ Failed to load ExploreElements:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    });

    const loadElementsByUser = flow(function* (uid: string, area?: Area) {
      try {
        setLoading(true);
        logger.debug(`Loading ExploreElements for user ${uid}...`);

        const cloudStorage = self.environment.cloudStorage;
        if (!cloudStorage) {
          throw new Error('CloudStorage not available');
        }

        const elementsData: any[] = yield cloudStorage.getExploreElementsByUser(
          uid,
          area
        );

        // Convert data to ExploreElement instances and add them
        elementsData.forEach((elementData) => {
          const parsedElementData = deepParseDates(elementData);
          const element = ExploreElementModel.create(
            parsedElementData,
            self.environment
          );
          addElement(element);
        });

        updateLastUpdated();
        logger.debug(`Loaded ${elementsData.length} ExploreElements for user`);
      } catch (error) {
        logger.error('Failed to load ExploreElements for user:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    });

    const createElement = flow(function* (elementData: ExploreElementCreateIn) {
      try {
        setLoading(true);
        logger.debug('Creating new ExploreElement...', elementData);

        const cloudStorage = self.environment.cloudStorage;
        if (!cloudStorage) {
          throw new Error('CloudStorage not available');
        }

        // Get current user UID using Firebase Auth directly (like other services do)
        const { getAuth } = require('../../util');
        const auth = getAuth();
        const currentUser = auth.currentUser;
        const userUid = currentUser?.uid || '';

        // id darf nicht im Payload sein, uid muss gesetzt werden
        const elementDataWithUid: ExploreElementCreateIn = {
          ...elementData,
          uid: userUid,
        };

        // Upload to Firebase und erhalte die neue ID
        const newId = yield cloudStorage.addExploreElement(elementDataWithUid);
        logger.debug('✅ Successfully uploaded to Firebase', {
          documentId: newId,
        });

        // Erstelle das lokale Model mit der von Firebase vergebenen ID
        const parsedElementData = deepParseDates({
          id: newId,
          name: elementData.name,
          type: elementData.type,
          startDate: elementData.startDate ? elementData.startDate : new Date(),
          endDate: elementData.endDate ? elementData.endDate : new Date(),
          favorite: false,
          country: elementData.country || '',
          gender: elementData.gender || '',
          age: elementData.age || 0,
          location: elementData.location || '',
          nationality: elementData.nationality || '',
          description: elementData.description || '',
          latitude: elementData.latitude || null,
          longitude: elementData.longitude || null,
          uid: userUid,
          isPublic:
            elementData.isPublic !== undefined ? elementData.isPublic : true,
        });
        const element = ExploreElementModel.create(
          parsedElementData,
          self.environment
        );

        addElement(element);
        updateLastUpdated();
        logger.debug(
          '✅ ExploreElement created successfully and added to store'
        );

        return element;
      } catch (error) {
        logger.error('Failed to create ExploreElement:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    });

    const updateElement = flow(function* (elementData: ExploreElementIn) {
      try {
        setLoading(true);
        logger.debug('Updating ExploreElement...', elementData);

        const cloudStorage = self.environment.cloudStorage;
        if (!cloudStorage) {
          throw new Error('CloudStorage not available');
        }

        // Update in Firebase
        yield cloudStorage.updateExploreElement(elementData);

        // Update local instance
        const parsedElementData = deepParseDates(elementData);
        const existingElement = self.getElementById(parsedElementData.id);
        if (existingElement) {
          Object.assign(existingElement, {
            name: parsedElementData.name,
            type: parsedElementData.type,
            startDate: parsedElementData.startDate
              ? parsedElementData.startDate
              : new Date(),
            endDate: parsedElementData.endDate
              ? parsedElementData.endDate
              : new Date(),
            country: parsedElementData.country || '',
            gender: parsedElementData.gender || '',
            age: parsedElementData.age || 0,
            location: parsedElementData.location || '',
            nationality: parsedElementData.nationality || '',
            description: parsedElementData.description || '',
            latitude: parsedElementData.latitude || null,
            longitude: parsedElementData.longitude || null,
          });
        } else {
          logger.warn('Element not found for update, creating new one');
          const { getAuth } = require('../../util');
          const auth = getAuth();
          const currentUser = auth.currentUser;
          const userUid = currentUser?.uid || '';
          const element = ExploreElementModel.create(
            deepParseDates({
              id: parsedElementData.id,
              name: parsedElementData.name,
              type: parsedElementData.type,
              startDate: parsedElementData.startDate,
              endDate: parsedElementData.endDate,
              favorite: false,
              country: parsedElementData.country || '',
              gender: parsedElementData.gender || '',
              age: parsedElementData.age || 0,
              location: parsedElementData.location || '',
              nationality: parsedElementData.nationality || '',
              description: parsedElementData.description || '',
              latitude: parsedElementData.latitude || null,
              longitude: parsedElementData.longitude || null,
              uid: userUid,
            }),
            self.environment
          );
          addElement(element);
        }

        updateLastUpdated();
        logger.debug('ExploreElement updated successfully');
        return existingElement || self.getElementById(elementData.id);
      } catch (error) {
        logger.error('Failed to update ExploreElement:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    });

    const deleteElement = flow(function* (id: string, area?: Area) {
      try {
        setLoading(true);
        logger.debug('Deleting ExploreElement...', id);

        const cloudStorage = self.environment.cloudStorage;
        if (!cloudStorage) {
          throw new Error('CloudStorage not available');
        }

        // Delete from Firebase
        yield cloudStorage.deleteExploreElement(id, area);

        // Remove from local store
        removeElement(id);
        updateLastUpdated();
        logger.debug('ExploreElement deleted successfully');
      } catch (error) {
        logger.error('Failed to delete ExploreElement:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    });

    const cleanupPastEvents = flow(function* () {
      try {
        logger.debug('Starting cleanup of past events...');
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day

        const pastEvents = self.elements.filter((element) => {
          if (element.endDate) {
            const endDate = new Date(element.endDate);
            return endDate < today;
          }
          return false;
        });

        logger.debug('Past events found:', pastEvents.length);

        // Remove past events from the store
        pastEvents.forEach((event) => {
          logger.debug('Removing past event:', event.id);
          removeElement(event.id);
        });

        logger.debug('Past events cleanup completed');
      } catch (error) {
        logger.error('Error during past events cleanup:', error);
      }
    });

    return {
      addElement,
      removeElement,
      clearAll,
      setLoading,
      updateLastUpdated,
      loadElements,
      loadElementsByUser,
      createElement,
      updateElement,
      deleteElement,
      cleanupPastEvents,
    };
  });

function deepParseDates(data: any): any {
  if (Array.isArray(data)) {
    return data.map((item) => deepParseDates(item));
  } else if (data && typeof data === 'object') {
    const newObj: any = {};
    Object.keys(data).forEach((key) => {
      const value = data[key];
      // Felder, die als Datum behandelt werden sollen
      const isDateField =
        key.endsWith('Date') ||
        key === 'joinedAt' ||
        key === 'lastRunAt' ||
        key === 'date';
      if (isDateField && value) {
        // Firestore Timestamp
        if (
          typeof value === 'object' &&
          value !== null &&
          typeof value.seconds === 'number'
        ) {
          newObj[key] = new Date(
            value.seconds * 1000 + (value.nanoseconds || 0) / 1e6
          );
        } else if (typeof value === 'string' || typeof value === 'number') {
          const d = new Date(value);
          newObj[key] = isNaN(d.getTime()) ? null : d;
        } else if (value instanceof Date) {
          newObj[key] = value;
        } else {
          newObj[key] = null;
        }
      } else if (Array.isArray(value)) {
        newObj[key] = value.map((item) => deepParseDates(item));
      } else if (typeof value === 'object' && value !== null) {
        newObj[key] = deepParseDates(value);
      } else {
        newObj[key] = value;
      }
    });
    return newObj;
  }
  return data;
}
